import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import dayjs from 'dayjs';

import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconWrapper,
  ModalContent,
  SectionWrapper,
  SectionHeader,
  SectionTitleRow,
  SectionTitle,
  SectionCount,
  SectionDivider,
  LockerCardsWrapper,
  LockerCardRow,
  StyledRadio,
  LockerCard,
  CardContent,
  CardTopRow,
  CardTopLeft,
  LockerNumber,
  LockerBadge,
  ProductName,
  CardBottomRow,
  ValidityWrapper,
  ValidityLabel,
  ValidityDate,
  RemainingDays,
  ModalFooter,
  CancelButton,
  ConfirmButton,
} from './LockerTicketSelectModalStyled';

/**
 * 연장 라커 선택 모달
 * @param {boolean} open - 모달 열림 상태
 * @param {function} onClose - 모달 닫기 핸들러
 * @param {function} onSelect - 자리 선택 완료 핸들러 (spot 전달)
 * @param {Array} lockerTickets - 구역 → 자리 → 이용권으로 묶인 목록
 *
 * 한 자리에 이용권이 여러 개면 카드 하나에 스택으로 보여준다 (회원상세 라커 카드와 같은 방식).
 * 연장 시작일은 그 자리의 마지막 이용 기간 다음 날 — 선택 후 판매등록에서 수정 가능.
 * 만료된 이용권은 서버에서 이미 제외돼 내려온다.
 */
const VISIBLE_PASS_ROWS = 2; // 카드 높이를 다른 카드와 맞추려고 기본 2줄만 보여준다

const LockerTicketSelectModal = ({ open, onClose, onSelect, lockerTickets = [] }) => {
  const { t } = useTranslation();
  const [selectedSpotKey, setSelectedSpotKey] = useState(null);
  const [expandedSpots, setExpandedSpots] = useState([]);

  // 모달 열릴 때 선택 초기화
  useEffect(() => {
    if (open) {
      setSelectedSpotKey(null);
      setExpandedSpots([]);
    }
  }, [open]);

  // 남은 일수 계산
  const calculateRemainingDays = endDate => {
    if (!endDate) return 0;
    const today = dayjs();
    const end = dayjs(endDate);
    return end.diff(today, 'day');
  };

  // 날짜 포맷
  const formatDate = date => {
    if (!date) return '-';
    return dayjs(date).format('YYYY.MM.DD');
  };

  // 선택된 자리 찾기
  const selectedSpot = useMemo(() => {
    if (!selectedSpotKey) return null;
    for (const area of lockerTickets) {
      const found = area.spots?.find(spot => spot.spotKey === selectedSpotKey);
      if (found) return found;
    }
    return null;
  }, [selectedSpotKey, lockerTickets]);

  const handleSpotClick = spotKey => {
    setSelectedSpotKey(spotKey);
  };

  // 확인 버튼 핸들러
  const handleConfirm = () => {
    if (selectedSpot && onSelect) {
      onSelect(selectedSpot);
    }
    onClose();
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    setSelectedSpotKey(null);
    onClose();
  };

  return (
    <StyledModal open={open} onClose={handleClose}>
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <ModalHeaderTitle>{t('연장 라커 선택')}</ModalHeaderTitle>
          <CloseIconWrapper onClick={handleClose}>
            <Close sx={{ width: '24px', height: '24px' }} />
          </CloseIconWrapper>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          {lockerTickets.map(area => (
            <SectionWrapper key={area.lockerAreaId}>
              {/* Section Header */}
              <SectionHeader>
                <SectionTitleRow>
                  <SectionTitle>{area.lockerAreaName}</SectionTitle>
                  <SectionCount>{area.spots?.length || 0}</SectionCount>
                </SectionTitleRow>
                <SectionDivider />
              </SectionHeader>

              {/* 자리 카드 — 이용권이 여러 개면 스택으로 */}
              <LockerCardsWrapper>
                {area.spots?.map(spot => {
                  const isSelected = selectedSpotKey === spot.spotKey;
                  const hasLocker = spot.lockerNumber !== null;
                  const multi = spot.passes.length > 1;
                  const isExpanded = expandedSpots.includes(spot.spotKey);
                  const hiddenCount = Math.max(0, spot.passes.length - VISIBLE_PASS_ROWS);
                  const lastPass = spot.passes[spot.passes.length - 1];
                  const remainingDays = calculateRemainingDays(spot.lastEndDate);
                  const isExpiringSoon = remainingDays <= 7;

                  return (
                    <LockerCardRow key={spot.spotKey}>
                      <StyledRadio checked={isSelected} onChange={() => handleSpotClick(spot.spotKey)} />
                      <LockerCard isSelected={isSelected} onClick={() => handleSpotClick(spot.spotKey)}>
                        <CardContent>
                          {/* Top Row */}
                          <CardTopRow>
                            <CardTopLeft>
                              <LockerNumber>
                                {hasLocker ? `${spot.lockerNumber}번라커` : t('미배정')}
                              </LockerNumber>
                            </CardTopLeft>
                            <LockerBadge>{t('라커')}</LockerBadge>
                          </CardTopRow>

                          {/* Product Name — 복수면 아래 스택에 이름이 나오므로 생략(중복) */}
                          {!multi && <ProductName>{lastPass?.productName}</ProductName>}

                          {multi ? (
                            /* 이용권 스택 — 카드 높이를 맞추려 기본 2줄, 나머지는 '외 N개'로 접는다 */
                            <div
                              style={{
                                border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden', marginTop: 6,
                              }}>
                              {(isExpanded ? spot.passes : spot.passes.slice(0, VISIBLE_PASS_ROWS)).map((pass, idx) => (
                                <div
                                  key={pass.ticketId}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 8, padding: '7px 10px', fontSize: 12,
                                    borderTop: idx > 0 ? '1px solid #ececec' : 'none',
                                    color: '#555',
                                  }}>
                                  <span style={{ fontWeight: 600, color: '#262626' }}>{pass.productName}</span>
                                  <span>
                                    {formatDate(pass.startDate)} - {formatDate(pass.endDate)}
                                  </span>
                                </div>
                              ))}
                              {hiddenCount > 0 && (
                                <div
                                  onClick={e => {
                                    e.stopPropagation();
                                    setExpandedSpots(prev =>
                                      prev.includes(spot.spotKey)
                                        ? prev.filter(key => key !== spot.spotKey)
                                        : [...prev, spot.spotKey]
                                    );
                                  }}
                                  style={{
                                    borderTop: '1px solid #ececec', padding: '6px 10px', textAlign: 'center',
                                    fontSize: 11.5, fontWeight: 600, color: '#0F7F85', cursor: 'pointer',
                                  }}>
                                  {isExpanded ? `${t('접기')} ▲` : `${t('외')} ${hiddenCount}${t('개 더보기')} ▼`}
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Bottom Row */
                            <CardBottomRow>
                              <ValidityWrapper>
                                <ValidityLabel>{t('유효기간')}</ValidityLabel>
                                <ValidityDate>
                                  {formatDate(spot.passes[0]?.startDate)} - {formatDate(spot.lastEndDate)}
                                </ValidityDate>
                              </ValidityWrapper>
                              <RemainingDays isExpiringSoon={isExpiringSoon}>
                                {remainingDays > 0 ? `${remainingDays}일 남음` : t('만료됨')}
                              </RemainingDays>
                            </CardBottomRow>
                          )}
                        </CardContent>
                      </LockerCard>
                    </LockerCardRow>
                  );
                })}
              </LockerCardsWrapper>
            </SectionWrapper>
          ))}
        </ModalContent>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={handleClose}>{t('취소')}</CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={!selectedSpotKey}>
            {t('선택완료')}
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  );
};

export default LockerTicketSelectModal;
